'use client';

import React, { FC, useEffect } from 'react';

import Image from 'next/image';

import ReactModal from 'react-modal';

import classNames from 'classnames';

import { useMatchesMediaQuery } from '@/hooks/useMatchesMediaQuery';

import close from '@/public/assets/icons/close.svg';

export type ModalProps = {
  handleClose: () => void;
  isOpen: boolean;
  children: React.ReactNode;
  contentClasses?: string;
  modalClasses?: {
    desktop?: {
      [key: string]: string;
    };
    mobile?: {
      [key: string]: string;
    };
  };
  overlayClasses?: {
    [key: string]: string;
  };
  ariaHideApp?: boolean;
  isCloseable?: boolean;
};

const Modal: FC<ModalProps> = ({
  handleClose,
  isOpen,
  children,
  contentClasses,
  modalClasses,
  overlayClasses,
  isCloseable = true,
  ariaHideApp = true,
}) => {
  const isMobile = useMatchesMediaQuery({
    query: `(max-width: 640px)`,
  });

  useEffect(() => {
    if (process.env.NODE_ENV !== 'test') ReactModal.setAppElement('main');
  }, []);

  const customStyles = {
    content: {
      height: 'fit-content',
      maxHeight: '100vh',
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
      padding: '1rem',
      border: 'none',
      borderRadius: '5px',
      boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.15)',
      maxWidth: 'auto',
      ...(isMobile ? modalClasses?.mobile : modalClasses?.desktop),
    },
    overlay: {
      zIndex: 1000,
      ...overlayClasses,
    },
  };

  if (isMobile) {
    customStyles.content.maxWidth = '90vw';
  }

  return (
    <ReactModal
      isOpen={isOpen}
      style={customStyles}
      onRequestClose={handleClose}
      ariaHideApp={ariaHideApp}
      shouldCloseOnEsc={isCloseable}
      shouldCloseOnOverlayClick={isCloseable}
    >
      <div className="flex flex-col items-center gap-2">
        {isCloseable && (
          <Image
            src={close}
            alt="close"
            onClick={handleClose}
            className="cursor-pointer self-end"
            data-testid="close-modal"
            width={24}
            height={24}
          />
        )}
        <div
          data-testid="modal-contents"
          className={classNames(contentClasses)}
        >
          {children}
        </div>
      </div>
    </ReactModal>
  );
};

export default React.memo(Modal);
